'use client';

import React from 'react';
import { Product, ProductBadgeKey, Translations } from '@frodo/models/types';
import { ConBadgeMolecule } from '@gandalf/babylon/molecules/badge';
import { SvgIcon } from '@gandalf/babylon/atoms/icon';
import BundleIcon from '@gandalf/babylon/dist/icons/bundle.svg';
import FaTagIcon from '@gandalf/babylon/dist/icons/fa-tag.svg';
import FlameIcon from '@gandalf/babylon/dist/icons/flame.svg';
import TrophyIcon from '@gandalf/babylon/dist/icons/trophy.svg';
import { ConBadgePurchasingAttrProps } from '@gandalf/babylon/molecules/badge/ConBadgePurchasingAttr';
import { isProductUnavailable } from '@frodo/lib/utils/common/productUtils';

const purchasingBadge = (
  text: string,
  color: ConBadgePurchasingAttrProps['backgroundColor'],
  icon?: SvgIcon,
) => (
  <ConBadgeMolecule.PurchasingAttr
    backgroundColor={color}
    label={text}
    icon={icon}
    size="medium"
  />
);

const seasonalBadge = (text: string, icon: SvgIcon) => (
  <ConBadgeMolecule.SeasonalPromo label={text} icon={icon} size="medium" />
);

const discountBadge = (text: string) => (
  <ConBadgeMolecule.Discount label={text} showIcon size="medium" />
);

const getBadgeByType = (type: ProductBadgeKey, text: string) =>
  ({
    topseller: purchasingBadge(text, 'yellow', FlameIcon),
    bestseller: purchasingBadge(text, 'yellow', FlameIcon),
    new: purchasingBadge(text, 'green'),
    loyalty: purchasingBadge(text, 'orange', TrophyIcon),
    offer: seasonalBadge(text, FaTagIcon),
    promo1: seasonalBadge(text, FaTagIcon),
    promo2: seasonalBadge(text, FaTagIcon),
    promo3: seasonalBadge(text, FaTagIcon),
    bundle: seasonalBadge(text, BundleIcon),
    bundle_bogo: seasonalBadge(text, BundleIcon),
    outlet_deal: seasonalBadge(text, FaTagIcon),
    bulk_price: discountBadge(text),
  })[type] || purchasingBadge(text, 'yellow', FlameIcon);

export type ProductBadgeProps = {
  product: Product;
  trans: Translations;
};

/** Returns badge UI or `null` — use when wrapping markup should only exist when a badge is shown. */
export const getProductBadge = (
  product: Product,
  trans: Translations,
): React.ReactNode => {
  if (isProductUnavailable(product)) {
    // Deliberately shows the same badge for deactivated, not-sellable, and
    // actually out-of-stock products — differentiating the copy needs
    // stakeholder-approved wording, tracked as a follow-up.
    return (
      <ConBadgeMolecule.Informative
        label={trans['product.outofstock-temp']}
        size="medium"
        color="red"
      />
    );
  }

  const variety = product.variety?.toLowerCase();
  const isBundle = variety === 'bundle';
  const freeItemsCount = product.freeItemsCount ?? 0;

  if (isBundle && freeItemsCount > 0) {
    return getBadgeByType(
      'bundle_bogo' as ProductBadgeKey,
      trans['badge.bundle_bogo'].replace('%count%', freeItemsCount.toString()),
    );
  }

  if (isBundle) {
    return getBadgeByType(
      variety as ProductBadgeKey,
      trans[`badge.${variety}`] ?? '',
    );
  }

  if (!product.badgeKey) return null;

  return getBadgeByType(
    product.badgeKey,
    trans[`badge.${product.badgeKey}`] ?? '',
  );
};

export const ProductBadge = ({ product, trans }: ProductBadgeProps) =>
  getProductBadge(product, trans);
